// The selected node, what the caller may do there, and the selected
// currency — resolved by the /_app shell's beforeLoad on EVERY navigation
// (memberships are re-read when stale, so a suspension or removal shows up
// on the next click) and handed to every screen through this context.
import { createContext, useContext, type ReactNode } from "react";
import type { CurrencyCode } from "@/lib/currency";
import type {
  AuthMe,
  BrokerBreadcrumbItem,
  StaffCapability,
  StaffMeResponse,
  StaffPathResponse,
  StaffScopeItem,
} from "@/lib/types";

export interface Scope {
  me: AuthMe;
  staff: StaffMeResponse;
  nodeId: string;
  /** Breadcrumb from the covering membership node down to nodeId. */
  path: StaffPathResponse;
  /** The selected node as the breadcrumb names it. */
  node: BrokerBreadcrumbItem | undefined;
  capabilities: StaffCapability[];
  /** The caller holds an ACTIVE membership ON this node (own-node capabilities apply). */
  onMembershipNode: boolean;
  membership: StaffScopeItem | undefined;
  currency: CurrencyCode;
  /** The currencies the node trades (lib/currency nodeCurrencies; SM-851): what the picker offers. */
  currencies: CurrencyCode[];
}

const ScopeContext = createContext<Scope | null>(null);

export function ScopeProvider({ value, children }: { value: Scope; children: ReactNode }) {
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope(): Scope {
  const v = useContext(ScopeContext);
  if (!v) throw new Error("useScope outside the /_app shell");
  return v;
}

/** The shell's search params, for links that keep node + currency. */
export function useShellSearch(): { node: string; currency: CurrencyCode } {
  const s = useScope();
  return { node: s.nodeId, currency: s.currency };
}

export function useCan(cap: StaffCapability): boolean {
  return useScope().capabilities.includes(cap);
}
